import { useCallback, useMemo } from 'react'
import { useCurrentUser, useShowStaff } from '@/api/hooks'
export const ROLE_CHOICES = [
  'admin',
  'organizer',
  'presiding_judge',
  'lead_secretary',
  'secretary',
  'steward',
  'judge',
  'viewer',
] as const
export type RoleChoice = (typeof ROLE_CHOICES)[number]

type PermissionMap = typeof PERMISSION_MAP
export type Permission = keyof PermissionMap

const PERMISSION_MAP = {
  'org:write': ['admin'],
  'show:create': ['admin'],
  'show:write': ['organizer', 'lead_secretary', 'admin'],
  'class:write': ['organizer', 'lead_secretary', 'secretary', 'admin'],
  'class:reindex': ['organizer', 'lead_secretary', 'secretary', 'admin'],
  'entrycodes:allocate': ['organizer', 'lead_secretary', 'secretary', 'admin'],
  'exhibitnos:reserve': ['organizer', 'lead_secretary', 'secretary', 'steward', 'admin'],
  'exhibitors:write': ['organizer', 'lead_secretary', 'secretary', 'steward', 'admin'],
  'entries:write': ['organizer', 'lead_secretary', 'secretary', 'steward', 'admin'],
  'judging:state:write': ['presiding_judge', 'organizer', 'lead_secretary', 'steward', 'admin'],
  'judging:scores:write': [
    'presiding_judge',
    'judge',
    'organizer',
    'lead_secretary',
    'secretary',
    'admin',
  ],
  'awards:finalize': ['presiding_judge', 'organizer', 'lead_secretary', 'admin'],
  'reports:leaderboard': [
    'presiding_judge',
    'organizer',
    'lead_secretary',
    'secretary',
    'judge',
    'admin',
  ],
  'reports:results': [
    'presiding_judge',
    'organizer',
    'lead_secretary',
    'secretary',
    'judge',
    'admin',
  ],
  'reports:finance': ['organizer', 'lead_secretary', 'secretary', 'admin'],
  'reports:entrant_packet': ['organizer', 'lead_secretary', 'secretary', 'admin'],
  'staff:write': ['organizer', 'lead_secretary', 'admin'],
  'judging:judges:read': ['organizer', 'lead_secretary', 'presiding_judge', 'secretary', 'admin'],
  'judging:judges:write': ['organizer', 'lead_secretary', 'presiding_judge', 'secretary', 'admin'],
  'people:merge': ['admin'],
} satisfies Record<string, RoleChoice[]>

function toRoleChoice(value: string | null | undefined): RoleChoice | null {
  if (!value) return null
  const normalised = value.toLowerCase()
  return ROLE_CHOICES.includes(normalised as RoleChoice) ? (normalised as RoleChoice) : null
}

export function formatRoleList(roles: readonly string[]): string {
  if (roles.length === 0) return 'No roles'
  if (roles.length === 1) return capitaliseRole(roles[0])
  const [leading, trailing] = [roles.slice(0, -1), roles.at(-1)!]
  return `${leading.map(capitaliseRole).join(', ')} or ${capitaliseRole(trailing)}`
}

function capitaliseRole(role: string) {
  return role
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function useActorPermissions(showId?: string) {
  const userQuery = useCurrentUser()
  const user = userQuery.data
  const staffRoles = useMemo(
    () =>
      (user?.showRoles ?? [])
        .filter(assignment => assignment.showId === showId)
        .map(assignment => toRoleChoice(assignment.role))
        .filter((role): role is RoleChoice => role !== null),
    [user?.showRoles, showId]
  )
  const allRoles = useMemo(
    () => new Set<RoleChoice>([...staffRoles, ...(user?.isAdmin ? ['admin' as const] : [])]),
    [staffRoles, user?.isAdmin]
  )
  const canReadStaff = ['admin', 'organizer', 'lead_secretary'].some(role =>
    allRoles.has(role as RoleChoice)
  )
  const staffQuery = useShowStaff(canReadStaff ? showId : undefined)

  const isAdmin = allRoles.has('admin')

  const can = useCallback(
    (permission: Permission) => {
      if (isAdmin) return true
      const allowed = PERMISSION_MAP[permission] ?? []
      return allowed.some(role => allRoles.has(role))
    },
    [isAdmin, allRoles]
  )

  const getAllowedRoles = useCallback(
    (permission: Permission) => PERMISSION_MAP[permission] ?? [],
    []
  )

  return {
    actorId: user?.personId ?? user?.id ?? '',
    isLoading: userQuery.isLoading,
    can,
    getAllowedRoles,
    roles: Array.from(allRoles),
    showRoles: staffRoles,
    isAdmin,
    staffQuery: {
      ...staffQuery,
      isLoading: userQuery.isLoading || (canReadStaff && staffQuery.isLoading),
    },
  }
}
